import { ALL_COUNTRIES_WITH_USA_FIRST } from "$lib/util/country"
import { isValidTaxNumber } from "$lib/yup/personal-info/tax-information"
import { phone } from "$lib/yup/util/phone-number"
import {
  MAX_SHARE,
  MIN_SHARE,
  entityBeneficialOwnerSchema,
  nationalIdentificationSchema,
} from "@tastyworks/tastyworks-api/validate"
import type { Alpha3Code } from "i18n-iso-countries/index"
import {
  array,
  boolean,
  mixed,
  number,
  object,
  string,
  type InferType,
} from "yup"

export const individualOwnerSchema = object({
  individualHasShare: boolean().required(),
  individualPercentageShare: number().when(["individualHasShare"], {
    is: true,
    then: (schema) => schema.required().min(MIN_SHARE).max(MAX_SHARE),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export const individualOwnerOtherSchema = object({
  individualOtherHasShare: boolean().required(),
})

export const otherEntityOwnershipSchema = object({
  entityOtherHasShare: boolean().required(),
})

export const beneficialOwnerNameSchema = entityBeneficialOwnerSchema.pick([
  "firstName",
  "middleName",
  "lastName",
])

export const beneficialOwnerDetailsSchema = entityBeneficialOwnerSchema
  .pick(["email", "birthDate", "ownershipPercent"])
  .concat(
    object({
      // Make required when backend ready - ATP-14144
      phoneNumber: phone().optional(),
    })
  )

const normalizeTaxNumber = (value: string) =>
  value.replace(/[\s-]/g, "").toUpperCase()

export function createBeneficialOwnerTaxInfoSchema(
  otherTaxNumbers: string[] = []
) {
  const taken = new Set(otherTaxNumbers.map(normalizeTaxNumber))

  return object({
    citizenshipCountry: mixed<Alpha3Code>()
      .required()
      .oneOf(ALL_COUNTRIES_WITH_USA_FIRST),
    residesInUS: boolean().required(),
    taxNumber: string()
      .test(
        "duplicate-tax-number",
        "yup.beneficialOwners.duplicateTaxNumber",
        (value) => !value || !taken.has(normalizeTaxNumber(value))
      )
      .when("residesInUS", {
        is: true,
        then: (schema) =>
          schema
            .transform((value) => value?.replaceAll("-", ""))
            .length(9)
            .test(
              "invalid-tax-number",
              "yup.common.taxInformation.invalidTaxNumber",
              isValidTaxNumber
            )
            .required(),
        otherwise: (schema) => schema.max(40).required(),
      }),
    useCustomerTaxNumber: boolean().optional(),
  })
}

export const beneficialOwnerTaxInfoSchema = createBeneficialOwnerTaxInfoSchema()

export const beneficialOwnerAddressSchema = entityBeneficialOwnerSchema.pick([
  "address",
])

export const beneficialOwnerIdentificationSchema = nationalIdentificationSchema

export const beneficialOwnerSchema = beneficialOwnerNameSchema
  .concat(beneficialOwnerDetailsSchema)
  .concat(beneficialOwnerTaxInfoSchema)
  .concat(
    object({
      identification: beneficialOwnerIdentificationSchema
        .optional()
        .when("residesInUS", {
          is: (residesInUS: boolean) => residesInUS === false,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional().default(undefined),
        }),
    })
  )
  .concat(beneficialOwnerAddressSchema)

export type BeneficialOwnerData = InferType<typeof beneficialOwnerSchema>

export const beneficialOwnersTableSchema = object({
  applicantPercentageShare: number().default(0),
  individualHasShare: boolean().default(false),
  shouldShowOwnerForm: boolean().default(false),
  beneficialOwners: array()
    .of(beneficialOwnerSchema)
    .ensure()
    .when(["shouldShowOwnerForm"], {
      is: false,
      then: (schema) =>
        schema
          // there is no separate field for applicantPercentageShare on beneficial-owners-table, so we
          // need to validate it along with beneficialOwners, otherwise editing percentage for applicant
          // doesn't get validated and the form can continue with invalid data
          .test({
            name: "applicant-min-share",
            test: function () {
              if (!this.parent.individualHasShare) return true
              const applicantPercentage =
                this.parent.applicantPercentageShare ?? 0
              if (applicantPercentage >= MIN_SHARE) return true
              return this.createError({
                message: {
                  key: "yup.common.number.min",
                  values: { min: MIN_SHARE },
                },
              })
            },
          })
          .test({
            name: "max-100%",
            message: "yup.common.number.maxOneHundredPercent",
            test: function (value) {
              if (!value || value.length === 0) {
                return true
              }

              const ownershipTotal = value.reduce(
                (sum, owner) => sum + (owner.ownershipPercent ?? 0),
                this.parent.applicantPercentageShare ?? 0
              )
              return ownershipTotal <= MAX_SHARE
            },
          }),
      otherwise: (schema) => schema,
    }),
})

export const beneficialOwnersSchema = object({
  individualOwnership: individualOwnerSchema,
  otherIndividualOwnership: individualOwnerOtherSchema,
  otherEntityOwnership: otherEntityOwnershipSchema,
  confirmNoBeneficialOwners: object({}),
  applicantIdentification: beneficialOwnerIdentificationSchema,
  beneficialOwnersTable: beneficialOwnersTableSchema,
  beneficialOwnerName: beneficialOwnerNameSchema,
  beneficialOwnerDetails: beneficialOwnerDetailsSchema,
  beneficialOwnerTaxInfo: beneficialOwnerTaxInfoSchema,
  beneficialOwnerIdentification: beneficialOwnerIdentificationSchema,
  beneficialOwnerAddress: beneficialOwnerAddressSchema,
})
